import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Text, Grid, Checkbox, Textarea } from "@mantine/core";
import { LuShieldCheck, LuStethoscope, LuInfo } from "react-icons/lu";

/*
 * StudioSectionsForm
 * ──────────────────
 * Renders a note-creation form directly from a Note-Type Studio template's rich
 * `sections[]` model, so the note a user fills in mirrors the exact layout the
 * template author built in the studio (section grouping, field widths, field
 * types, options, required flags, conditional visibility and calculated fields).
 *
 * It binds every control to the parent Mantine `form` by the field's key — its
 * label for most types, but a fixed name (LOCKED_KEY) for the FIRST client/
 * staff/facility field, so renaming its display label in the studio can't
 * change what key the answer submits under. A template may hold more than one
 * of those; the extras are independent questions and key off their own
 * label/`key` like any other field. This is the same key the studio persists into
 * `custom[].field`, so the existing submit path (`obj.fields = { ...form.values }`)
 * keeps working with no backend change.
 *
 * Only used for user-authored custom templates; the special built-in note types
 * (Incident Report, Progress Note, Essential Support, Billing, Intervention,
 * Group) keep their hand-built layouts in NewNoteDesignCopy.
 */

const isFilled = (v) =>
  !(v == null || v === "" || (Array.isArray(v) && !v.length));

// Parse a time — a Date, a 24-hour "HH:mm[:ss]" string, or a 12-hour
// "h:mm[:ss] am/pm" string — into a Date (the date part is arbitrary).
// Returns null when it can't be parsed.
const parseTime = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([aApP][mM])?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : 0;
  const ap = m[4] ? m[4].toUpperCase() : null;
  if (ap) {
    if (h === 12) h = ap === "AM" ? 0 : 12;
    else if (ap === "PM") h += 12;
  }
  const d = new Date(2000, 0, 1, h, min, sec);
  return isNaN(d) ? null : d;
};

// Display format: 12-hour "hh:mm:ss AM/PM", matching the built-in note types.
// Leaves unparseable values as-is so a half-typed string isn't clobbered.
const to12h = (value) => {
  const d = parseTime(value);
  if (!d) return value == null ? "" : String(value);
  return d.toLocaleTimeString("en-US", {
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Storage format: canonical 24-hour "HH:mm:ss". This is what the note table,
// report views and the PDF all parse with moment("HH:mm:ss"), so the value we
// submit MUST be 24-hour even though the drawer shows it as 12-hour.
const to24h = (value) => {
  const d = parseTime(value);
  if (!d) return value == null ? "" : String(value);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// Client/staff/facility fields always save under this fixed name, no matter
// what display label the note type was built with, so Add Note keeps writing
// to the same key it always has ("Client Name" / "Staff Name" / "Facility Name").
const LOCKED_KEY = {
  client: "Client Name",
  staff: "Staff Name",
  facility: "Facility Name",
};
// ...but only the FIRST client/staff/facility field in the template does. A
// template may hold more than one, and the extras are independent questions
// with their own answer, keyed off their label like any other field.
//
// `key` is only set when a field's label couldn't be the storage key — two
// sections are allowed to reuse a name, but their answers must not collide, so
// the studio resolves the later one to "<name> (<section>)" and persists it
// there (and in the matching custom[].field).
//
// This MIRRORS fieldStorageKeys() in the studio's schema.js and has to stay in
// step with it: that function decides the key a note type SAVES under, this one
// decides the key the note-creation form READS and WRITES. Any drift and an
// answer lands somewhere the rest of the app doesn't look. Reimplemented rather
// than imported so this component keeps no dependency on the studio module.
export const fieldKeysOf = (sections) => {
  const keys = {};
  const taken = new Set();
  const claim = (k) => taken.add(String(k).trim().toLowerCase());
  const isTaken = (k) => taken.has(String(k).trim().toLowerCase());

  const fields = (sections || []).flatMap((s) =>
    (s.fields || []).map((f) => ({ f, s })),
  );

  const labelCounts = {};
  fields.forEach(({ f }) => {
    const l = (f.label || "").trim().toLowerCase();
    if (l) labelCounts[l] = (labelCounts[l] || 0) + 1;
  });

  // Pass 1a — an extra client/staff/facility field keeps its pinned key and
  // doesn't compete for the locked one (so a section reorder can't move
  // "Client Name" onto it).
  fields.forEach(({ f }) => {
    const locked = LOCKED_KEY[f.type];
    const persisted = (f.key || "").trim();
    if (locked && persisted && persisted !== locked) {
      keys[f.id] = persisted;
      claim(persisted);
    }
  });
  // Pass 1b — the locked key goes to the first field of that type left over.
  const lockedClaimed = new Set();
  fields.forEach(({ f }) => {
    const locked = LOCKED_KEY[f.type];
    if (!locked || keys[f.id] || lockedClaimed.has(f.type)) return;
    lockedClaimed.add(f.type);
    keys[f.id] = locked;
    claim(locked);
  });
  // Pass 1c — an ordinary field's pinned key, while its label is ambiguous.
  fields.forEach(({ f }) => {
    if (keys[f.id]) return;
    const persisted = (f.key || "").trim();
    const label = (f.label || "").trim();
    if (persisted && label && labelCounts[label.toLowerCase()] > 1) {
      keys[f.id] = persisted;
      claim(persisted);
    }
  });

  // Pass 2 — everything else derives from its label, suffixed with the section
  // title if that label is already spoken for.
  fields.forEach(({ f, s }) => {
    if (keys[f.id]) return;
    const base = (f.label || "").trim();
    if (!base) {
      keys[f.id] = base;
      return;
    }
    let key = base;
    if (isTaken(key)) {
      const title = (s.title || "").trim();
      const scoped = title ? `${base} (${title})` : base;
      key = scoped;
      let n = 2;
      while (isTaken(key)) key = `${scoped} ${n++}`;
    }
    claim(key);
    keys[f.id] = key;
  });

  return keys;
};

// Section-icon SVG paths — mirror the studio builder's icon set so the section
// heading shows the exact icon the template author picked (SECTION_ICONS in
// src/pages/Home/NoteType/studio/schema.js). Custom uploads use an "img:" URL.
const SECTION_IC = {
  tag: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  doc: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>',
  cross:
    '<path d="M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h5v5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2z"/>',
  shield: '<path d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10"/>',
  notif:
    '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  calc: '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8M8 10h2M14 10h2M8 14h2M14 14h2M8 18h2M14 18h2"/>',
  careplan:
    '<path d="M9 3h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1V4a1 1 0 0 1 1-1z"/><path d="M12 11.5c-.6-1.2-2.5-1.1-2.5.4 0 1 1.2 1.8 2.5 3 1.3-1.2 2.5-2 2.5-3 0-1.5-1.9-1.6-2.5-.4z"/>',
};

const SecIcon = ({ icon, size = 15 }) => {
  if (icon && icon.indexOf("img:") === 0)
    return (
      <img
        src={icon.slice(4)}
        alt=""
        style={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: 3,
        }}
      />
    );
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: SECTION_IC[icon] || SECTION_IC.tag }}
    />
  );
};

/* ── reference-design controls (custom HTML/CSS, no native inputs) ───────────
 * These replace the Mantine Select/Radio/Checkbox/DateInput so both the
 * Note-Type Studio preview and the real note-create drawer render the exact
 * control styling from notes-redesign-combined (.pv* / .dd / .dpk look). */
const Chevron = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const CheckMark = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const CalIcon = () => (
  <svg
    width={17}
    height={17}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const ClockIcon = () => (
  <svg
    width={17}
    height={17}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const NavIcon = ({ dir }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={dir < 0 ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
  </svg>
);

// Severity dot ramp (mirrors the studio's chipColor ramp)
const CHIP_RAMP = [
  "#12B76A",
  "#F79009",
  "#F04438",
  "#D92D20",
  "#3538CD",
  "#7A5AF8",
  "#0BA5EC",
];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const fmtDate = (d) =>
  d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

// Custom dropdown — styled trigger + popover menu, closes on outside click.
const SsfSelect = ({
  value,
  options = [],
  placeholder = "Select…",
  onChange,
  view,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`ssf-dd${open ? " open" : ""}`} ref={ref}>
      <button
        type="button"
        className={`ssf-dd-btn${sel ? "" : " ph"}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={view}
        onClick={() => !view && setOpen((o) => !o)}
      >
        <span className="ssf-dd-val">{sel ? sel.label : placeholder}</span>
        <Chevron />
      </button>
      {open ? (
        <div className="ssf-dd-menu" role="listbox">
          {options.length ? (
            options.map((o) => (
              <div
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`ssf-dd-opt${o.value === value ? " sel" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span>{o.label}</span>
                {o.value === value ? (
                  <span className="ssf-chk">
                    <CheckMark />
                  </span>
                ) : null}
              </div>
            ))
          ) : (
            <div className="ssf-dd-empty">No options</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Custom multi-select (client / staff) — chips with remove + searchable popover.
const SsfMultiSelect = ({
  value,
  options = [],
  placeholder = "Select…",
  tone,
  onChange,
  view,
  allowSelectAll = false,
  labelMap = null,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  // Resolve a chip's label from the selectable options first, then a full
  // name map (so a selected value filtered out of the options — e.g. an
  // auto-selected admin in the staff field — still shows a name, not its id).
  const chips = arr.map(
    (val) =>
      options.find((o) => o.value === val) ||
      (labelMap && labelMap[val] != null
        ? { value: val, label: labelMap[val] }
        : { value: val, label: val }),
  );
  const remaining = options.filter(
    (o) =>
      !arr.includes(o.value) &&
      (!q ||
        String(o.label || "")
          .toLowerCase()
          .includes(q.toLowerCase())),
  );
  const staff = tone === "staff";
  // "Select all" picks every selectable (non-disabled) option, merged with the
  // current selection so nothing already chosen is dropped.
  const selectableValues = options
    .filter((o) => !o.disabled)
    .map((o) => o.value);
  const allSelected =
    selectableValues.length > 0 &&
    selectableValues.every((val) => arr.includes(val));
  return (
    <div className={`ssf-ms${open ? " open" : ""}`} ref={ref}>
      <div
        className="ssf-ms-box"
        onClick={() => {
          if (!view) setOpen((o) => !o);
        }}
      >
        {chips.map((o) => (
          <span key={o.value} className={`ssf-ms-chip${staff ? " staff" : ""}`}>
            {o.label}
            {!view ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(arr.filter((x) => x !== o.value));
                }}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {!arr.length ? <span className="ssf-ms-ph">{placeholder}</span> : null}
        <span className="ssf-ms-caret">
          <Chevron />
        </span>
      </div>
      {open && !view ? (
        <div className="ssf-ms-menu">
          <input
            className="ssf-ms-search"
            placeholder="Search…"
            value={q}
            autoFocus
            onChange={(e) => setQ(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {allowSelectAll && selectableValues.length && !allSelected ? (
            <div
              className="ssf-ms-opt ssf-ms-all"
              onClick={() => {
                onChange(Array.from(new Set([...arr, ...selectableValues])));
                setQ("");
              }}
            >
              Select All
            </div>
          ) : null}
          {remaining.length ? (
            remaining.map((o) => (
              <div
                key={o.value}
                className="ssf-ms-opt"
                onClick={() => {
                  onChange([...arr, o.value]);
                  setQ("");
                }}
              >
                {o.label}
              </div>
            ))
          ) : allowSelectAll && allSelected ? (
            <div className="ssf-ms-empty">All selected</div>
          ) : (
            <div className="ssf-ms-empty">No options</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Custom date field — button + month calendar (mirrors the Calendar module).
const SsfDate = ({ value, onChange, view }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selDate =
    value instanceof Date ? value : value ? new Date(value) : null;
  const seed = selDate && !isNaN(selDate) ? selDate : new Date();
  const [ym, setYm] = useState({ y: seed.getFullYear(), m: seed.getMonth() });
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const toggle = () => {
    if (view) return;
    const b = selDate && !isNaN(selDate) ? selDate : new Date();
    setYm({ y: b.getFullYear(), m: b.getMonth() });
    setOpen((o) => !o);
  };
  const nav = (delta) =>
    setYm((s) => {
      let m = s.m + delta,
        y = s.y;
      if (m < 0) {
        m = 11;
        y -= 1;
      }
      if (m > 11) {
        m = 0;
        y += 1;
      }
      return { y, m };
    });
  const first = new Date(ym.y, ym.m, 1);
  const startDow = first.getDay();
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < startDow; i += 1)
    cells.push(
      <button
        key={`e${i}`}
        type="button"
        className="ssf-dpk-cell out"
        disabled
      />,
    );
  for (let d = 1; d <= days; d += 1) {
    const dt = new Date(ym.y, ym.m, d);
    const isT = dt.toDateString() === today.toDateString();
    const isS =
      selDate &&
      !isNaN(selDate) &&
      dt.toDateString() === selDate.toDateString();
    cells.push(
      <button
        key={d}
        type="button"
        className={`ssf-dpk-cell${isT ? " today" : ""}${isS ? " sel" : ""}`}
        onClick={() => {
          onChange(dt);
          setOpen(false);
        }}
      >
        {d}
      </button>,
    );
  }
  return (
    <div className="ssf-dd" ref={ref}>
      <button
        type="button"
        className={`ssf-date${selDate && !isNaN(selDate) ? "" : " ph"}`}
        disabled={view}
        onClick={toggle}
      >
        <span>
          {selDate && !isNaN(selDate) ? fmtDate(selDate) : "Select date"}
        </span>
        <CalIcon />
      </button>
      {open ? (
        <div className="ssf-dpk">
          <div className="ssf-dpk-head">
            <button
              type="button"
              className="ssf-dpk-nav"
              onClick={() => nav(-1)}
            >
              <NavIcon dir={-1} />
            </button>
            <div className="ssf-dpk-title">
              {first.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </div>
            <button
              type="button"
              className="ssf-dpk-nav"
              onClick={() => nav(1)}
            >
              <NavIcon dir={1} />
            </button>
          </div>
          <div className="ssf-dpk-dow">
            {DOW.map((x) => (
              <span key={x}>{x}</span>
            ))}
          </div>
          <div className="ssf-dpk-grid">{cells}</div>
        </div>
      ) : null}
    </div>
  );
};

// Custom time field — a free-text input that DISPLAYS a 12-hour "hh:mm:ss
// AM/PM" value (matching the built-in note types) but STORES the canonical
// 24-hour "HH:mm:ss" string that the note table, report views and PDF parse
// with moment("HH:mm:ss"). Text is kept raw while editing and reformatted on
// blur. The legacy prefill in NewNoteDesignCopy already writes 24-hour here, so
// that stays consistent.
const SsfTime = ({ value, placeholder, view, onChange }) => {
  const focusedRef = useRef(false);
  const [text, setText] = useState(() => to12h(value));

  // Mirror external value changes (prefill / note load) into the input while
  // not editing, showing them as 12-hour. If the stored value isn't already
  // canonical 24-hour, rewrite it so what submits stays parseable everywhere.
  // to24h is idempotent, so this settles after one rewrite.
  useEffect(() => {
    if (focusedRef.current) return;
    setText(to12h(value));
    const store = to24h(value);
    if (!view && store && store !== value) onChange(store);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="ssf-time">
      <span className="ssf-time-ic">
        <ClockIcon />
      </span>
      <input
        className="ssf-ctl ssf-time-in"
        placeholder={placeholder || "hh:mm:ss AM/PM"}
        value={text}
        readOnly={view}
        disabled={view}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => setText(e.currentTarget.value)}
        onBlur={() => {
          focusedRef.current = false;
          setText(to12h(text));
          onChange(to24h(text));
        }}
      />
    </div>
  );
};

// Scoped styles for the custom controls (uses the app's --app-* design tokens).
const SSF_CSS = `
.ssf-root .ssf-ctl{width:100%;min-height:42px;border:1px solid var(--app-border);border-radius:8px;padding:9px 12px;font:inherit;font-size:14px;color:var(--app-text);outline:none;background:var(--app-surface)}
.ssf-root textarea.ssf-ctl{min-height:90px;resize:vertical}
.ssf-root .ssf-ctl:focus{border-color:var(--app-primary);box-shadow:0 0 0 3px rgba(255,175,74,.2)}
.ssf-root .ssf-ctl:disabled,.ssf-root .ssf-ctl[readonly]{background:var(--app-surface)}
.ssf-root .ssf-time{position:relative}
.ssf-root .ssf-time-ic{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#667085;pointer-events:none;display:flex}
.ssf-root .ssf-time-in{padding-left:38px}
.ssf-root .ssf-seg{display:flex;gap:8px;flex-wrap:wrap}
.ssf-root .ssf-seg button{border:1px solid var(--app-border);background:var(--app-surface);border-radius:8px;padding:9px 14px;font:inherit;font-size:13px;color:var(--app-text-muted);cursor:pointer;display:inline-flex;align-items:center;gap:7px}
.ssf-root .ssf-seg button[aria-pressed="true"]{background:var(--app-tint-primary,#FFF6E9);border-color:var(--app-primary-bd,#FFE2B8);color:#7A4A00;font-weight:600}
.ssf-root .ssf-seg button:disabled{cursor:default;opacity:.9}
.ssf-root .ssf-dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex:none}
.ssf-root .ssf-dd{position:relative}
.ssf-root .ssf-dd-btn,.ssf-root .ssf-date{width:100%;min-height:42px;border:1px solid var(--app-border);border-radius:8px;padding:9px 12px;font:inherit;font-size:14px;color:var(--app-text);background:var(--app-surface);display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;text-align:left}
.ssf-root .ssf-dd-btn:disabled,.ssf-root .ssf-date:disabled{cursor:default}
.ssf-root .ssf-dd-btn.ph .ssf-dd-val,.ssf-root .ssf-date.ph{color:var(--app-text-faint,#98A2B3)}
.ssf-root .ssf-dd-val{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ssf-root .ssf-dd-btn>svg,.ssf-root .ssf-date>svg{flex:none;color:#667085;transition:transform .16s ease}
.ssf-root .ssf-dd.open .ssf-dd-btn{border-color:var(--app-primary);box-shadow:0 0 0 3px rgba(255,175,74,.2)}
.ssf-root .ssf-dd.open .ssf-dd-btn>svg{transform:rotate(180deg)}
.ssf-root .ssf-dd-menu{position:absolute;z-index:60;top:calc(100% + 6px);left:0;right:0;background:var(--app-surface);border:1px solid var(--app-border);border-radius:10px;box-shadow:0 12px 32px rgba(16,24,40,.16);padding:5px;max-height:260px;overflow:auto}
.ssf-root .ssf-dd-opt{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:9px 11px;border-radius:7px;font-size:14px;color:var(--app-text);cursor:pointer}
.ssf-root .ssf-dd-opt:hover{background:var(--app-bg)}
.ssf-root .ssf-dd-opt.sel{font-weight:600}
.ssf-root .ssf-dd-opt .ssf-chk{flex:none;color:var(--app-primary);display:grid;place-items:center}
.ssf-root .ssf-dd-empty{padding:9px 11px;color:var(--app-text-faint,#98A2B3);font-size:13px}
.ssf-root .ssf-calc{min-height:42px;border:1px solid #CDEBD9;background:#ECFDF3;border-radius:8px;display:flex;align-items:center;padding:0 12px;font-size:15px;font-weight:700;color:#027A48;font-variant-numeric:tabular-nums}
.ssf-root .ssf-ms{position:relative}
.ssf-root .ssf-ms-box{min-height:42px;border:1px solid var(--app-border);border-radius:8px;padding:6px 40px 6px 8px;display:flex;flex-wrap:wrap;gap:6px;align-items:center;background:var(--app-surface);cursor:pointer;position:relative}
.ssf-root .ssf-ms.open .ssf-ms-box{border-color:var(--app-primary);box-shadow:0 0 0 3px rgba(255,175,74,.2)}
.ssf-root .ssf-ms-chip{display:inline-flex;align-items:center;gap:4px;background:var(--app-tint-primary,#FFF6E9);border:1px solid var(--app-primary-bd,#FFE2B8);color:#7A4A00;border-radius:16px;padding:3px 4px 3px 11px;font-size:13px}
.ssf-root .ssf-ms-chip.staff{background:#F1F5F9;border-color:#E2E8F0;color:#334155}
.ssf-root .ssf-ms-chip button{border:none;background:none;color:inherit;cursor:pointer;opacity:.55;font-size:15px;line-height:1;padding:0 4px}
.ssf-root .ssf-ms-chip button:hover{opacity:1}
.ssf-root .ssf-ms-ph{color:var(--app-text-faint,#98A2B3);font-size:14px;padding:2px 4px}
.ssf-root .ssf-ms-caret{position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#667085;pointer-events:none;display:grid;place-items:center;transition:transform .16s ease}
.ssf-root .ssf-ms.open .ssf-ms-caret{transform:translateY(-50%) rotate(180deg)}
.ssf-root .ssf-ms-menu{position:absolute;z-index:60;top:calc(100% + 6px);left:0;right:0;background:var(--app-surface);border:1px solid var(--app-border);border-radius:10px;box-shadow:0 12px 32px rgba(16,24,40,.16);padding:5px;max-height:240px;overflow:auto}
.ssf-root .ssf-ms-search{width:100%;border:1px solid var(--app-border);border-radius:7px;padding:7px 9px;font:inherit;font-size:13px;color:var(--app-text);outline:none;margin-bottom:4px;background:var(--app-surface)}
.ssf-root .ssf-ms-all{font-weight:600;color:var(--app-primary,#B26A00)}
.ssf-root .ssf-ms-opt{padding:9px 11px;border-radius:7px;font-size:14px;color:var(--app-text);cursor:pointer}
.ssf-root .ssf-ms-opt:hover{background:var(--app-bg)}
.ssf-root .ssf-ms-empty{padding:9px 11px;color:var(--app-text-faint,#98A2B3);font-size:13px}
.ssf-root .ssf-dpk{position:absolute;z-index:80;top:calc(100% + 6px);left:0;width:266px;background:var(--app-surface);border:1px solid var(--app-border);border-radius:12px;box-shadow:0 16px 40px rgba(16,24,40,.2);padding:12px}
.ssf-root .ssf-dpk-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
.ssf-root .ssf-dpk-title{font-size:13.5px;font-weight:700;color:var(--app-text)}
.ssf-root .ssf-dpk-nav{border:1px solid var(--app-border);background:var(--app-surface);border-radius:7px;width:28px;height:28px;display:grid;place-items:center;color:var(--app-text-muted);cursor:pointer}
.ssf-root .ssf-dpk-nav:hover{background:var(--app-bg);color:var(--app-text)}
.ssf-root .ssf-dpk-dow{display:grid;grid-template-columns:repeat(7,1fr);margin-bottom:4px}
.ssf-root .ssf-dpk-dow span{text-align:center;font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--app-text-faint,#98A2B3);padding:4px 0}
.ssf-root .ssf-dpk-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}
.ssf-root .ssf-dpk-cell{height:32px;border:none;background:transparent;border-radius:8px;font:inherit;font-size:13px;font-weight:600;color:var(--app-text);cursor:pointer;display:grid;place-items:center}
.ssf-root .ssf-dpk-cell:hover{background:var(--app-tint-primary,#FFF6E9)}
.ssf-root .ssf-dpk-cell.out{color:var(--app-border);cursor:default}
.ssf-root .ssf-dpk-cell.today{box-shadow:inset 0 0 0 1.5px var(--app-primary-bd,#FFE2B8)}
.ssf-root .ssf-dpk-cell.sel{background:var(--app-primary);color:#000}
`;

const StudioSectionsForm = ({
  sections = [],
  form,
  classes,
  view = false,
  edit = false,
  // More than one client on the note → the per-client care plan doesn't apply,
  // so the "Use care plan?" field (and any care-plan section) is hidden the same
  // way the built-in Progress / Essential Support notes hide their support-plan
  // toggle. The drawer clears the field's answer when it flips this on.
  hideCarePlan = false,
  onClientChange,
  onFacilityChange,
}) => {
  const { allPatients } = useSelector((s) => s?.patient) || {};
  const { staff } = useSelector((s) => s?.staff) || {};
  const { allFacilities } = useSelector((s) => s?.facility) || {};

  // Storage key per field. Recomputed per render rather than memoized:
  // `sections` is a plain prop off the note type and this walk is a handful of
  // fields.
  const storageKeys = fieldKeysOf(sections);
  const fieldKey = (f) => storageKeys[f.id] || f.key || f.label;
  // The note's canonical client/staff/facility — the one every downstream
  // consumer reads. Only it drives selectedPatient, the facility scope and the
  // care plan; extra fields of the same type just store their own selection.
  const isPrimary = (f) => !!LOCKED_KEY[f.type] && fieldKey(f) === LOCKED_KEY[f.type];

  const clientOptions = (allPatients || []).map((p) => ({
    value: p?.id,
    label: p?.name,
    disabled: !p?.active,
  }));
  // Only active, non-deleted members whose type is "staff" (excludes admins /
  // contractors), matching the general note types' staff picker.
  const staffOptions = (staff?.staffs || [])
    .filter(
      (s) => s?.active === true && s?.deleted !== true && s?.type === "staff",
    )
    .map((s) => ({
      value: s?.id,
      label: s?.name,
    }));
  // Full id→name map over ALL staff (incl. admins), so an auto-selected member
  // who isn't in the filtered options above still shows their name as a chip.
  const staffLabelMap = (staff?.staffs || []).reduce((acc, s) => {
    if (s?.id != null) acc[s.id] = s?.name;
    return acc;
  }, {});
  const facilityOptions = (allFacilities?.facilities || []).map((f) => ({
    value: f?.id,
    label: f?.name,
  }));

  // ── conditional visibility (ported from the studio preview) ──
  const val = (label) => form?.values?.[label];
  const fieldHolds = (label, op, value) => {
    const v = val(label);
    switch (op) {
      case "is":
        return v === value;
      case "is not":
        return v !== value;
      case "greater than":
        return Number(v) > Number(value);
      case "less than":
        return Number(v) < Number(value);
      case "contains":
        return String(v || "")
          .toLowerCase()
          .includes(String(value || "").toLowerCase());
      case "includes":
        return Array.isArray(v) && v.includes(value);
      case "does not include":
        return !(Array.isArray(v) && v.includes(value));
      case "is empty":
        return !isFilled(v);
      case "is filled":
      case "is not empty":
      case "has response":
        return isFilled(v);
      default:
        return true;
    }
  };
  const condOn = (c) => {
    if (!c || !c.rules?.length) return true;
    return c.mode === "any"
      ? c.rules.some(ruleHolds)
      : c.rules.every(ruleHolds);
  };
  const sectionCondMet = (sec) => condOn(sec.cond);
  function ruleHolds(r) {
    if (String(r.field).startsWith("SEC::")) {
      const sec = sections.find((s) => s.id === r.field.slice(5));
      if (!sec) return true;
      const vals = sec.fields.map((f) => val(fieldKey(f)));
      switch (r.op) {
        case "is empty":
          return !vals.some(isFilled);
        case "has response":
          return vals.some(isFilled);
        case "is complete":
          return sec.fields
            .filter((f) => f.required)
            .every((f) => isFilled(val(fieldKey(f))));
        case "is shown":
          return sectionCondMet(sec);
        case "is hidden":
          return !sectionCondMet(sec);
        default:
          return true;
      }
    }
    return fieldHolds(r.field, r.op, r.value);
  }

  // ── calculated fields ──
  const evalCalc = (f) => {
    if ((f.calc?.type || "number") !== "number") return "—";
    let expr = (f.calc?.formula || "").replace(/\{([^}]+)\}/g, (m, r) => {
      const v = Number(val(r.trim()));
      return isNaN(v) ? 0 : v;
    });
    expr = expr.replace(/×/g, "*").replace(/÷/g, "/");
    if (!expr.trim() || !/^[0-9.+\-*/() ]+$/.test(expr)) return "—";
    try {
      // Input is regex-restricted to digits, operators and parens — safe to eval.
      // eslint-disable-next-line no-new-func
      return (
        Math.round(Function('"use strict";return(' + expr + ")")() * 100) / 100
      );
    } catch {
      return "—";
    }
  };

  // Keep calc field values in the form state so they get submitted.
  const allFields = sections.flatMap((s) => s.fields || []);
  // Change-detection key for the calc effect. Guarded so a value that can't be
  // serialized (e.g. a stray event/DOM node) can never crash and blank the form.
  let valuesKey;
  try {
    valuesKey = JSON.stringify(form?.values || {});
  } catch {
    valuesKey = String(Object.keys(form?.values || {}).length);
  }
  useEffect(() => {
    allFields
      .filter((f) => f.type === "calc")
      .forEach((f) => {
        const computed = evalCalc(f);
        const next = computed === "—" ? "" : computed;
        if (form?.values?.[fieldKey(f)] !== next)
          form.setFieldValue(fieldKey(f), next);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesKey]);

  // Pre-fill date fields with today's date and time fields with the current
  // time when creating a new note (never when viewing/editing an existing one),
  // only for fields left empty — so they save with sensible defaults.
  const didDefaultRef = useRef(false);
  useEffect(() => {
    if (view || edit || didDefaultRef.current || !form) return;
    const now = new Date();
    // Canonical 24-hour "HH:mm:ss" for storage (SsfTime shows it as 12-hour).
    const timeStr = to24h(now);
    allFields.forEach((f) => {
      if (f.type === "date" && !isFilled(form.values[fieldKey(f)]))
        form.setFieldValue(fieldKey(f), now);
      if (f.type === "time" && !isFilled(form.values[fieldKey(f)]))
        form.setFieldValue(fieldKey(f), timeStr);
    });
    didDefaultRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, edit, sections]);

  const set = (label, v) => form?.setFieldValue(label, v);

  // ── notifications field: load the selected client's contacts on file ──
  // The client id(s) come from any `client`-type field in the template.
  const clientLabels = allFields
    .filter((f) => f.type === "client")
    .map((f) => fieldKey(f));
  const selectedClientIds = clientLabels
    .map((l) => form?.values?.[l])
    .filter(Boolean)
    .flatMap((v) => (Array.isArray(v) ? v : [v]));

  const getInitials = (n) =>
    (n || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "?";

  const contactsForClient = (patient) => {
    const clientName = patient?.name || "";
    const guardians = [];
    const guardianName = `${patient?.form2?.guardianFirstName || ""} ${
      patient?.form2?.guardianLastName || ""
    }`.trim();
    if (guardianName)
      guardians.push({
        key: `${patient?.id}-guardian`,
        name: guardianName,
        sub: [clientName, "Guardian"].filter(Boolean).join(" · "),
        phone: patient?.form2?.emergencyContactPhoneNumber || "",
      });

    const medicalProviders = [];
    if (patient?.form2?.pcpName)
      medicalProviders.push({
        key: `${patient?.id}-pcp`,
        name: patient.form2.pcpName,
        sub: [clientName, "Primary Physician"].filter(Boolean).join(" · "),
        phone: patient?.form2?.pcpPhoneNumber || "",
      });
    (patient?.form2?.additionalProvider || []).forEach((prov, i) => {
      if (prov?.providerName)
        medicalProviders.push({
          key: `${patient?.id}-additional-${i}`,
          name: prov.providerName,
          sub: [clientName, prov?.providerType || "Provider"]
            .filter(Boolean)
            .join(" · "),
          phone: prov?.providerPhone || "",
        });
    });
    return { guardians, medicalProviders };
  };

  const renderNotifications = (f) => {
    const stored = form?.values?.[fieldKey(f)] || {};
    const notified = stored.notified || {};
    const notes = stored.notes || "";
    const update = (patch) => set(fieldKey(f), { notified, notes, ...patch });
    const toggle = (key) =>
      !view && update({ notified: { ...notified, [key]: !notified[key] } });

    const selectedClients = selectedClientIds
      .map((id) => allPatients?.find((p) => p?.id === id))
      .filter(Boolean);

    const entries = { guardians: [], medicalProviders: [] };
    selectedClients.forEach((p) => {
      const g = contactsForClient(p);
      entries.guardians.push(...g.guardians);
      entries.medicalProviders.push(...g.medicalProviders);
    });

    const groupDefs = [
      {
        key: "guardians",
        label: "GUARDIANS & LEGAL REPRESENTATIVES",
        icon: <LuShieldCheck />,
        iconBg: "rgb(238, 240, 253)",
        iconColor: "rgb(83, 92, 202)",
      },
      {
        key: "medicalProviders",
        label: "MEDICAL PROVIDERS",
        icon: <LuStethoscope />,
        iconBg: "rgb(236, 253, 243)",
        iconColor: "rgb(2, 122, 72)",
      },
    ];

    return (
      <Box>
        <Box className={classes?.incidentNotifyHint}>
          <LuInfo size={14} />
          Contacts on file for the selected client(s). Check everyone who was
          notified.
        </Box>
        {selectedClients.length === 0 ? (
          <Text className={classes?.incidentNotifySub}>
            Select a client above to see their contacts here.
          </Text>
        ) : (
          groupDefs.map((group) => {
            const list = entries[group.key];
            const count = list.filter((e) => notified[e.key]).length;
            return (
              <Box key={group.key}>
                <Box className={classes?.incidentNotifyGroupHeader}>
                  <Box
                    className={classes?.incidentNotifyGroupIcon}
                    style={{ background: group.iconBg, color: group.iconColor }}
                  >
                    {group.icon}
                  </Box>
                  <Text className={classes?.incidentNotifyGroupLabel}>
                    {group.label}
                  </Text>
                  <Box className={classes?.incidentNotifyGroupDivider} />
                  <Text className={classes?.incidentNotifyGroupCount}>
                    {count} of {list.length} notified
                  </Text>
                </Box>
                {list.length === 0 ? (
                  <Text className={classes?.incidentNotifySub} py={8}>
                    None on file.
                  </Text>
                ) : (
                  list.map((entry) => (
                    <Box
                      key={entry.key}
                      className={classes?.incidentNotifyRow}
                      onClick={() => toggle(entry.key)}
                    >
                      <Checkbox
                        color="yellow"
                        className={classes?.incidentNotifyCheckbox}
                        checked={!!notified[entry.key]}
                        onChange={() => toggle(entry.key)}
                        disabled={view}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box className={classes?.incidentNotifyAvatar}>
                        {getInitials(entry.name)}
                      </Box>
                      <Box>
                        <Text className={classes?.incidentNotifyName}>
                          {entry.name}
                        </Text>
                        <Text className={classes?.incidentNotifySub}>
                          {entry.sub}
                        </Text>
                      </Box>
                      {entry.phone && (
                        <Text className={classes?.incidentNotifyPhone}>
                          {entry.phone}
                        </Text>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            );
          })
        )}
        <Text fz={13} fw={500} c="#344054" mt={14} mb={6} ff="Inter">
          Notification notes{" "}
          <span style={{ fontWeight: 400, color: "#737373" }}>(optional)</span>
        </Text>
        <Textarea
          autosize
          minRows={3}
          placeholder="Who was contacted, when, by what method, and their response."
          value={notes}
          onChange={(e) => update({ notes: e.currentTarget.value })}
          readOnly={view}
          disabled={view}
        />
      </Box>
    );
  };

  // Single-select segmented pills (single / toggle / severity chips).
  const seg = (items, cur, onPick) => (
    <div className="ssf-seg">
      {items.map((o) => (
        <button
          type="button"
          key={o.value}
          aria-pressed={cur === o.value}
          disabled={view}
          onClick={() => !view && onPick(o.value)}
        >
          {o.dot ? (
            <span className="ssf-dot" style={{ background: o.dot }} />
          ) : null}
          {o.label}
        </button>
      ))}
    </div>
  );
  // Multi-select segmented pills (multiple choice).
  const multiSeg = (options, cur, onPick) => {
    const arr = Array.isArray(cur) ? cur : [];
    return (
      <div className="ssf-seg">
        {options.map((o) => {
          const on = arr.includes(o);
          return (
            <button
              type="button"
              key={o}
              aria-pressed={on}
              disabled={view}
              onClick={() =>
                !view && onPick(on ? arr.filter((x) => x !== o) : [...arr, o])
              }
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  };

  const renderControl = (f) => {
    const label = fieldKey(f);
    const v = val(label);
    const ph = f.placeholder || "";
    const opts = (f.options || []).map((o) => ({ value: o, label: o }));
    const common = { readOnly: view, disabled: view };
    switch (f.type) {
      case "textarea":
        return (
          <textarea
            className="ssf-ctl"
            placeholder={ph}
            value={v || ""}
            readOnly={view}
            onChange={(e) => set(label, e.currentTarget.value)}
            {...common}
          />
        );
      case "number":
        return (
          <input
            className="ssf-ctl"
            inputMode="numeric"
            placeholder={ph || "0"}
            value={
              v === "" || v == null ? "" : typeof v === "number" ? v : Number(v)
            }
            onChange={(e) => set(label, e.currentTarget.value)}
            {...common}
          />
        );
      case "date":
        return (
          <SsfDate value={v} view={view} onChange={(d) => set(label, d)} />
        );
      case "time":
        return (
          <SsfTime
            value={v}
            placeholder={ph}
            view={view}
            onChange={(nv) => set(label, nv)}
          />
        );
      case "dropdown":
        return (
          <SsfSelect
            value={v || null}
            options={opts}
            placeholder={ph || "Select…"}
            view={view}
            onChange={(nv) => set(label, nv)}
            {...common}
          />
        );
      case "single":
        return seg(
          (f.options || []).map((o) => ({ value: o, label: o })),
          v || "",
          (nv) => set(label, nv),
        );
      case "multi":
        return multiSeg(f.options || [], v, (nv) => set(label, nv));
      case "toggle":
      case "careplan":
        return seg(
          [
            { value: "No", label: "No" },
            { value: "Yes", label: "Yes" },
          ],
          v === "Yes" ? "Yes" : "No",
          (nv) => set(label, nv),
        );
      case "chips":
        return seg(
          (f.options || []).map((o, i) => ({
            value: o,
            label: o,
            dot: CHIP_RAMP[i % CHIP_RAMP.length],
          })),
          v || (f.options || [])[0] || "",
          (nv) => set(label, nv),
        );
      case "calc":
        return <div className="ssf-calc">{evalCalc(f)}</div>;
      case "client":
        return (
          <SsfMultiSelect
            value={v}
            options={clientOptions}
            placeholder="Select client(s)…"
            tone="client"
            view={view}
            allowSelectAll
            onChange={(nv) =>
              // Only the primary client field is reported up to the drawer —
              // that's what moves selectedPatient, the facility scope and the
              // care plan. An extra one is just another answer on the note.
              onClientChange && isPrimary(f)
                ? onClientChange(nv, label)
                : set(label, nv)
            }
          />
        );
      case "staff":
        return (
          <SsfMultiSelect
            value={v}
            options={staffOptions}
            placeholder="Select staff…"
            tone="staff"
            view={view}
            allowSelectAll
            labelMap={staffLabelMap}
            onChange={(nv) => set(label, nv)}
            {...common}
          />
        );
      case "facility":
        return (
          <SsfSelect
            value={v || null}
            options={facilityOptions}
            placeholder="Select facility…"
            view={view}
            onChange={(nv) =>
              // Same rule as client: only the primary facility field drives the
              // drawer's selectedFacility (care-plan and medication lookups).
              onFacilityChange && isPrimary(f)
                ? onFacilityChange(nv, label)
                : set(label, nv)
            }
            {...common}
          />
        );
      case "notifications":
        return renderNotifications(f);
      default:
        return (
          <input
            className="ssf-ctl"
            placeholder={ph}
            value={v || ""}
            readOnly={view}
            onChange={(e) => set(label, e.currentTarget.value)}
            {...common}
          />
        );
    }
  };

  return (
    <div className="ssf-root">
      <style>{SSF_CSS}</style>
      {sections.filter(sectionCondMet).map((sec) => {
        const visible = (sec.fields || [])
          .filter((f) => !(hideCarePlan && f.type === "careplan"))
          .filter((f) => condOn(f.conditional));
        // A care-plan section with nothing left to show goes too — otherwise
        // multi-client notes keep an empty "Care Plan" card and its blurb.
        if (!visible.length && (!sec.carePlan || hideCarePlan)) return null;
        return (
          <Box className={classes?.detailsForm} key={sec.id} mt={5}>
            <Box className={classes?.ch2}>
              <SecIcon icon={sec.icon} size={15} />
              {sec.title || "Section"}
            </Box>
            {sec.carePlan && !hideCarePlan ? (
              <Text
                fz={12.5}
                c="var(--app-text-muted, #737373)"
                mb={visible.length ? 10 : 0}
              >
                Pulls the client's active support-plan outcomes at note time.
              </Text>
            ) : null}
            <Grid mt={5}>
              {visible.map((f) => (
                <Grid.Col
                  key={f.id}
                  xs={12}
                  sm={12}
                  md={f.span || 12}
                  lg={f.span || 12}
                  xl={f.span || 12}
                >
                  <Text fz={13} fw={500} c="#344054" mb={6} ff="Inter">
                    {f.label}
                    {f.required ? (
                      <span style={{ color: "var(--danger, #E03131)" }}>
                        {" "}
                        *
                      </span>
                    ) : null}
                  </Text>
                  {renderControl(f)}
                  {f.help ? (
                    <Text fz={11.5} c="var(--app-text-muted, #737373)" mt={5}>
                      {f.help}
                    </Text>
                  ) : null}
                </Grid.Col>
              ))}
            </Grid>
          </Box>
        );
      })}
    </div>
  );
};

export default StudioSectionsForm;
